import { FC, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Flex,
	Input,
	Row,
	Select,
	Space,
	Table,
	Tag,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
	getAllUsers,
	getAllRoles,
	getAllDivisions,
	AdminUserData,
} from '@/libs/fleet-api/services/admin';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import CreateUserModal, { useCreateUserModal } from './modals/CreateUserModal';
import EditUserModal, { useEditUserModal } from './modals/EditUserModal';
import FleetTitle from '@/libs/antd/components/Title';

const { Search } = Input;
const { Option } = Select;

const AdminUserListPage: FC = () => {
	const [searchText, setSearchText] = useState('');
	const [roleFilter, setRoleFilter] = useState<string | undefined>();
	const [statusFilter, setStatusFilter] = useState<string | undefined>();

	const { data: users, isLoading: usersLoading } = useQuery({
		queryKey: ['admin-users'],
		queryFn: getAllUsers,
	});

	const { data: roles } = useQuery({
		queryKey: ['admin-roles'],
		queryFn: getAllRoles,
	});

	const { data: divisions } = useQuery({
		queryKey: ['admin-divisions'],
		queryFn: getAllDivisions,
	});

	const createUserModalHook = useCreateUserModal();
	const editUserModalHook = useEditUserModal();

	const filteredUsers = users?.data?.filter((user) => {
		const matchesSearch =
			searchText === '' ||
			user.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
			user.username.toLowerCase().includes(searchText.toLowerCase()) ||
			user.email.toLowerCase().includes(searchText.toLowerCase());

		const matchesRole = !roleFilter || user.group.code === roleFilter;
		const matchesStatus =
			!statusFilter ||
			(statusFilter === 'active' ? user.isActive : !user.isActive);

		return matchesSearch && matchesRole && matchesStatus;
	});

	const columns = [
		{
			title: 'Nama Lengkap',
			dataIndex: 'fullName',
			key: 'fullName',
			render: (text: string, record: AdminUserData) => (
				<Space>
					<div>
						<div style={{ fontWeight: 'bold' }}>{text}</div>
						<div style={{ fontSize: '12px', color: '#666' }}>
							{record.username}
						</div>
					</div>
				</Space>
			),
		},
		{
			title: 'Email',
			dataIndex: 'email',
			key: 'email',
		},
		{
			title: 'Role',
			dataIndex: 'group',
			key: 'group',
			render: (group: { name: string; code: string }) => (
				<Tag color="blue">{group.name}</Tag>
			),
		},
		{
			title: 'Divisi',
			dataIndex: 'division',
			key: 'division',
			render: (division: { name: string } | undefined) => (
				<span>{division ? division.name : '-'}</span>
			),
		},
		{
			title: 'Atasan',
			dataIndex: 'reportTo',
			key: 'reportTo',
			render: (reportTo: { fullName: string }) =>
				reportTo ? reportTo.fullName : '-',
		},
		{
			title: 'Status',
			dataIndex: 'isActive',
			key: 'isActive',
			render: (isActive: boolean) => (
				<Tag color={isActive ? 'green' : 'red'}>
					{isActive ? 'Aktif' : 'Tidak Aktif'}
				</Tag>
			),
		},
		{
			title: 'Aksi',
			key: 'actions',
			render: (_: unknown, record: AdminUserData) => (
				<Space>
					<Button
						type="link"
						icon={<EditOutlined />}
						onClick={() => editUserModalHook.open({ user: record })}
					>
						Edit
					</Button>
					{/* <Button
						type="link"
						onClick={() => handleToggleStatus(record)}
						loading={updateUserMutation.isPending}
					>
						{record.isActive ? 'Nonaktifkan' : 'Aktifkan'}
					</Button> */}
				</Space>
			),
		},
	];

	return (
		<div style={{ width: '100%' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '24px',
				}}
			>
				<div style={{ width: '100%' }}>
					<FleetTitle level={1}>
						<Flex
							style={{ gap: 10 }}
							justify="space-between"
							align="center"
						>
							<span>User Management</span>
							<Button
								type="primary"
								size="small"
								icon={<PlusOutlined />}
								onClick={() => createUserModalHook.open()}
							>
								Add User
							</Button>
						</Flex>
					</FleetTitle>
				</div>
			</div>

			<Card>
				<Row
					gutter={[16, 16]}
					style={{ marginBottom: '16px' }}
				>
					<Col
						xs={24}
						sm={12}
						md={8}
					>
						<Search
							placeholder="Cari nama, username, atau email..."
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							allowClear
							style={{ width: '100%' }}
						/>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
					>
						<Select
							placeholder="Filter berdasarkan role"
							value={roleFilter}
							onChange={setRoleFilter}
							allowClear
							style={{ width: '100%' }}
						>
							{roles?.data?.map((role) => (
								<Option
									key={role.id}
									value={role.code}
								>
									{role.name}
								</Option>
							))}
						</Select>
					</Col>
					<Col
						xs={24}
						sm={12}
						md={8}
					>
						<Select
							placeholder="Filter berdasarkan status"
							value={statusFilter}
							onChange={setStatusFilter}
							allowClear
							style={{ width: '100%' }}
						>
							<Option value="active">Aktif</Option>
							<Option value="inactive">Tidak Aktif</Option>
						</Select>
					</Col>
				</Row>

				<Table
					columns={columns}
					dataSource={filteredUsers}
					loading={usersLoading}
					rowKey="userId"
					scroll={{ x: 'max-content' }}
					pagination={{
						total: filteredUsers?.length,
						showSizeChanger: false,
						// showTotal: (total, range) =>
						// 	`${range[0]}-${range[1]} dari ${total} pengguna`,
					}}
				/>
			</Card>

			<CreateUserModal
				modal={createUserModalHook.modal}
				divisions={divisions?.data}
				onClose={() => createUserModalHook.close()}
			/>

			<EditUserModal
				modal={editUserModalHook.modal}
				data={editUserModalHook.data}
				divisions={divisions?.data}
				onClose={() => editUserModalHook.close()}
			/>
		</div>
	);
};

export default AdminUserListPage;
